import * as React from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { track } from "@/lib/track";
import { LocaleSwitcher } from "./LocaleSwitcher";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";
import { useLocale } from "@/hooks/useLocale";
import { DEFAULT_LOCALE } from "@translations/config";

interface FooterLink {
  labelKey: string;
  href: string;
  external?: boolean;
}

interface FooterCol {
  titleKey: string;
  links: FooterLink[];
}

const cols: FooterCol[] = [
  {
    titleKey: "common.footer.columns.services",
    links: [
      { labelKey: "common.footer.links.aiTransformation", href: "#services" },
      { labelKey: "common.footer.links.aiAugmentedDevelopment", href: "#services" },
      { labelKey: "common.footer.links.technicalAudit", href: "#services" },
      { labelKey: "common.footer.links.ctoAsService", href: "#services" },
    ],
  },
  {
    titleKey: "common.footer.columns.company",
    links: [
      { labelKey: "common.footer.links.about", href: "/about" },
      { labelKey: "common.footer.links.process", href: "/process" },
      { labelKey: "common.footer.links.pricing", href: "#pricing" },
      {
        labelKey: "common.footer.links.contact",
        href: "mailto:hello@holymoly.dev",
        external: true,
      },
    ],
  },
  {
    titleKey: "common.footer.columns.resources",
    links: [
      { labelKey: "common.footer.links.caseStudies", href: "/work" },
      { labelKey: "common.footer.links.faq", href: "#faq" },
    ],
  },
  {
    titleKey: "common.footer.columns.contact",
    links: [
      {
        labelKey: "common.footer.links.contact",
        href: "mailto:hello@holymoly.dev",
        external: true,
      },
      {
        labelKey: "common.footer.links.bookCall",
        href: "https://www.cal.eu/andreacab/holymoly-free-call?user=andreacab",
        external: true,
      },
    ],
  },
];

// One literal email + book-a-call URL appear twice as both label and href.
// Render them verbatim instead of translating, since they're identifiers.
const RAW_LABELS: Record<string, string | undefined> = {
  "common.footer.links.contact": undefined,
};

function getDisplayLabel(t: (k: string) => string, labelKey: string, href: string): string {
  // For the contact column, the rendered label is the email itself.
  if (labelKey === "common.footer.links.contact" && href.startsWith("mailto:")) {
    return href.replace("mailto:", "");
  }
  if (RAW_LABELS[labelKey]) return RAW_LABELS[labelKey]!;
  return t(labelKey);
}

function SocialLink({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink-2 transition-colors hover:border-line-strong hover:text-ink"
    >
      {children}
    </a>
  );
}

export function Footer() {
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  const locale = useLocale();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const landingPath = locale === DEFAULT_LOCALE ? "/" : `/${locale}`;
  const onLanding = pathname === landingPath;

  const handleNav = (href: string, external?: boolean) => {
    if (external) return;
    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="border-t border-line bg-page-bg">
      <div className="container-narrow px-6 py-16 lg:py-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.4fr_2fr] lg:gap-16">
          {/* Brand block */}
          <div>
            <div className="flex items-center">
              <span className="wordmark text-[18px]">
                holy<em>moly</em>
                <span className="align-baseline text-[0.85em] font-normal tracking-normal text-ink-2">
                  .dev
                </span>
              </span>
            </div>
            <p className="mt-4 max-w-[40ch] text-[14px] leading-[1.6] text-ink-2">
              {t("common.footer.description")}
            </p>
            <div className="mt-5 flex items-center gap-2">
              <SocialLink href="https://github.com/holy-moly-lab" label="GitHub">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 .3a12 12 0 0 0-3.8 23.4c.6.1.8-.3.8-.6v-2c-3.3.7-4-1.6-4-1.6-.6-1.4-1.4-1.8-1.4-1.8-1.1-.7.1-.7.1-.7 1.2.1 1.9 1.3 1.9 1.3 1 1.8 2.8 1.3 3.4 1 .1-.8.4-1.3.8-1.6-2.7-.3-5.5-1.3-5.5-6 0-1.3.5-2.4 1.3-3.2-.2-.4-.6-1.6.1-3.2 0 0 1-.3 3.3 1.2a11.5 11.5 0 0 1 6 0c2.3-1.5 3.3-1.2 3.3-1.2.7 1.6.3 2.8.1 3.2.8.8 1.3 2 1.3 3.2 0 4.6-2.8 5.6-5.5 5.9.5.4.9 1.1.9 2.3v3.4c0 .3.2.7.8.6A12 12 0 0 0 12 .3z" />
                </svg>
              </SocialLink>
              <SocialLink href="https://www.linkedin.com/company/holymolydev/" label="LinkedIn">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.5 2h-17A1.5 1.5 0 0 0 2 3.5v17A1.5 1.5 0 0 0 3.5 22h17a1.5 1.5 0 0 0 1.5-1.5v-17A1.5 1.5 0 0 0 20.5 2zM8 19H5v-9h3v9zM6.5 8.3c-1 0-1.7-.8-1.7-1.7s.7-1.7 1.7-1.7 1.7.8 1.7 1.7-.7 1.7-1.7 1.7zM19 19h-3v-4.7c0-1.1 0-2.6-1.6-2.6S12.5 13 12.5 14.3V19h-3v-9h2.9v1.3c.4-.8 1.4-1.6 2.9-1.6 3.1 0 3.7 2 3.7 4.7V19z" />
                </svg>
              </SocialLink>
            </div>
          </div>

          {/* Columns */}
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
            {cols.map((c) => (
              <div key={c.titleKey}>
                <h4 className="mono text-[11px] tracking-[0.16em] text-ink-3 uppercase">
                  {t(c.titleKey)}
                </h4>
                <ul className="mt-4 space-y-2.5">
                  {c.links.map((l) => {
                    const label = getDisplayLabel(t, l.labelKey, l.href);
                    const isInternalRoute = !l.external && l.href.startsWith("/");
                    if (isInternalRoute) {
                      return (
                        <li key={`${c.titleKey}-${l.labelKey}-${l.href}`}>
                          <Link
                            to={localizedHref(l.href)}
                            className="text-[13.5px] text-ink-2 transition-colors hover:text-ink"
                          >
                            {label}
                          </Link>
                        </li>
                      );
                    }
                    // For #-anchor links, when off the landing page we route
                    // back to the locale's home + hash so the browser lands
                    // and scrolls to the section. On the landing page we keep
                    // smooth scroll.
                    const isHash = !l.external && l.href.startsWith("#");
                    const href = isHash && !onLanding ? `${landingPath}${l.href}` : l.href;
                    return (
                      <li key={`${c.titleKey}-${l.labelKey}-${l.href}`}>
                        <a
                          href={href}
                          onClick={(e) => {
                            if (isHash && onLanding) {
                              e.preventDefault();
                              handleNav(l.href, l.external);
                            }
                            if (l.external && l.href.includes("cal.eu")) {
                              track("cta_click", { source: "footer" });
                            }
                          }}
                          target={l.external ? "_blank" : undefined}
                          rel={l.external ? "noopener noreferrer" : undefined}
                          className="text-[13.5px] text-ink-2 transition-colors hover:text-ink"
                        >
                          {label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-line pt-6 text-[12px] text-ink-3 sm:flex-row sm:items-center lg:mt-16">
          <span>{t("common.footer.copyright", { year: new Date().getFullYear() })}</span>
          <div className="flex items-center gap-6">
            <Link to={localizedHref("/privacy")} className="transition-colors hover:text-ink">
              {t("common.footer.privacy")}
            </Link>
            <Link to={localizedHref("/terms")} className="transition-colors hover:text-ink">
              {t("common.footer.terms")}
            </Link>
            <LocaleSwitcher />
            <span className="inline-flex items-center gap-1.5">
              {t("common.footer.status")} ·{" "}
              <span className="text-[#16a34a]">● {t("common.footer.statusOperational")}</span>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
